/**
 * Role-Based Access Control service for frontend validation
 * This mirrors the backend RBAC logic for consistent permission checking
 */
export class RBACService {
    /**
     * Check workspace-level permissions
     */
    static canManageWorkspace(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || workspace.role === "admin";
    }
    static canInviteUsers(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || workspace.role === "admin";
    }
    static canManageMembers(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || workspace.role === "admin";
    }
    static canDeleteWorkspace(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner; // Only owner can delete workspace
    }
    static canViewMembers(workspace) {
        return !!workspace; // All workspace members can view other members
    }
    /**
     * Check project-level permissions
     */
    static canCreateProjects(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || ["admin", "project_manager"].includes(workspace.role);
    }
    static canManageProjects(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || ["admin", "project_manager"].includes(workspace.role);
    }
    static canViewProjects(workspace) {
        return !!workspace; // All workspace members can view projects (filtered by assignment)
    }
    /**
     * Check task-level permissions
     */
    static canCreateTasks(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || ["admin", "project_manager"].includes(workspace.role);
    }
    static canManageTasks(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || ["admin", "project_manager"].includes(workspace.role);
    }
    static canUpdateTaskStatus(workspace) {
        return !!workspace; // All members can update status of assigned tasks
    }
    static canAssignTasks(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || ["admin", "project_manager"].includes(workspace.role);
    }
    /**
     * Check file-level permissions
     */
    static canUploadFiles(workspace) {
        return !!workspace; // All members can upload files to assigned tasks
    }
    static canDeleteFiles(workspace) {
        if (!workspace)
            return false;
        return workspace.isOwner || ["admin", "project_manager"].includes(workspace.role);
    }
    /**
     * Get role hierarchy (higher number = more permissions)
     */
    static getRoleLevel(workspace) {
        if (!workspace)
            return 0;
        if (workspace.isOwner)
            return 100; // Owner has highest level
        const roleLevels = {
            admin: 80,
            project_manager: 60,
            client: 40,
            contractor: 20,
        };
        return roleLevels[workspace.role] || 0;
    }
    /**
     * Check if user can manage another member
     */
    static canManageMember(workspace, targetMember, currentUserId) {
        if (!workspace)
            return false;
        // Can't manage yourself
        if (targetMember.userId === currentUserId)
            return false;
        // Can't manage workspace owner
        if (targetMember.userId === workspace.ownerId)
            return false;
        // Owner can manage anyone (except other owners)
        if (workspace.isOwner)
            return true;
        // Admin can manage non-admin roles
        if (workspace.role === "admin" && targetMember.role !== "admin")
            return true;
        return false;
    }
    /**
     * Get available roles for invitation based on current user's role
     */
    static getAvailableRoles(workspace) {
        if (!workspace)
            return [];
        const allRoles = [
            {
                value: "admin",
                label: "Admin",
                description: "Full access to workspace management and all projects",
            },
            {
                value: "project_manager",
                label: "Project Manager",
                description: "Can create and manage projects and tasks",
            },
            {
                value: "client",
                label: "Client",
                description: "Can view assigned projects and provide feedback",
            },
            {
                value: "contractor",
                label: "Contractor",
                description: "Can work on assigned tasks and update status",
            },
        ];
        // Owner can assign any role
        if (workspace.isOwner) {
            return allRoles;
        }
        // Admin can assign non-admin roles
        if (workspace.role === "admin") {
            return allRoles.filter((role) => role.value !== "admin");
        }
        // Other roles cannot invite users
        return [];
    }
    /**
     * Get role display information
     */
    static getRoleInfo(role) {
        const roleMap = {
            admin: {
                label: "Admin",
                description: "Full access to workspace management and all projects",
                color: "purple",
                bgColor: "bg-purple-100",
                textColor: "text-purple-700",
            },
            project_manager: {
                label: "Project Manager",
                description: "Can create and manage projects and tasks",
                color: "blue",
                bgColor: "bg-blue-100",
                textColor: "text-blue-700",
            },
            client: {
                label: "Client",
                description: "Can view assigned projects and provide feedback",
                color: "green",
                bgColor: "bg-green-100",
                textColor: "text-green-700",
            },
            contractor: {
                label: "Contractor",
                description: "Can work on assigned tasks and update status",
                color: "orange",
                bgColor: "bg-orange-100",
                textColor: "text-orange-700",
            },
        };
        return (roleMap[role] || {
            label: "Unknown",
            description: "Unknown role",
            color: "gray",
            bgColor: "bg-gray-100",
            textColor: "text-gray-700",
        });
    }
    /**
     * Get permission summary for current user
     */
    static getPermissionSummary(workspace) {
        return {
            workspace: {
                canManage: this.canManageWorkspace(workspace),
                canInvite: this.canInviteUsers(workspace),
                canDelete: this.canDeleteWorkspace(workspace),
            },
            projects: {
                canCreate: this.canCreateProjects(workspace),
                canManage: this.canManageProjects(workspace),
                canView: this.canViewProjects(workspace),
            },
            tasks: {
                canCreate: this.canCreateTasks(workspace),
                canManage: this.canManageTasks(workspace),
                canAssign: this.canAssignTasks(workspace),
                canUpdateStatus: this.canUpdateTaskStatus(workspace),
            },
            files: {
                canUpload: this.canUploadFiles(workspace),
                canDelete: this.canDeleteFiles(workspace),
            },
            members: {
                canView: this.canViewMembers(workspace),
                canManage: this.canManageMembers(workspace),
            },
        };
    }
    /**
     * Validate action before API call
     */
    static validateAction(workspace, action) {
        const permissions = this.getPermissionSummary(workspace);
        switch (action) {
            case "manage_workspace":
                return {
                    allowed: permissions.workspace.canManage,
                    reason: permissions.workspace.canManage ? undefined : "Admin role required",
                };
            case "invite_users":
                return {
                    allowed: permissions.workspace.canInvite,
                    reason: permissions.workspace.canInvite ? undefined : "Admin role required",
                };
            case "delete_workspace":
                return {
                    allowed: permissions.workspace.canDelete,
                    reason: permissions.workspace.canDelete ? undefined : "Workspace owner required",
                };
            case "create_projects":
                return {
                    allowed: permissions.projects.canCreate,
                    reason: permissions.projects.canCreate ? undefined : "Project Manager role or higher required",
                };
            case "manage_projects":
                return {
                    allowed: permissions.projects.canManage,
                    reason: permissions.projects.canManage ? undefined : "Project Manager role or higher required",
                };
            default:
                return { allowed: false, reason: "Unknown action" };
        }
    }
}
